import type { Child } from '@baby-tracker/contracts';
import {
    calendarDateInZone,
    lengthToMillimetres,
    massToGrams,
    type LengthDisplayUnit,
    type MassDisplayUnit,
} from '@baby-tracker/validation';
import { lazy, Suspense, useState, type ReactElement, type SubmitEvent } from 'react';

import { useCreateGrowth, useDeleteGrowth, useGrowth } from '../../../hooks/useRecords.js';
import type { GrowthIndicator } from '../../../lib/who-growth.js';
import { Button } from '../../ui/Button.js';
import { Card } from '../../ui/Card.js';
import { errorText } from '../../ui/error-text.js';
import { FormError } from '../../ui/FormError.js';
import fieldStyles from '../../ui/fields.module.css';
import { SegmentedControl } from '../../ui/SegmentedControl.js';
import { TextField } from '../../ui/TextField.js';
import styles from './insights.module.css';

const indicators = [
    { value: 'weight', label: 'Weight' },
    { value: 'length', label: 'Length' },
    { value: 'head', label: 'Head' },
] as const satisfies readonly { value: GrowthIndicator; label: string }[];

const GrowthCurves = lazy(() =>
    import('./GrowthCurves.js').then((module) => ({ default: module.GrowthCurves })),
);

export function GrowthSection({
    familyId,
    child,
    timeZone,
    massUnit,
    lengthUnit,
    canLog,
}: {
    familyId: string;
    child: Child;
    timeZone: string;
    massUnit: MassDisplayUnit;
    lengthUnit: LengthDisplayUnit;
    canLog: boolean;
}): ReactElement {
    const growth = useGrowth(familyId, child.id);
    const create = useCreateGrowth(familyId);
    const remove = useDeleteGrowth(familyId);
    const today = calendarDateInZone(new Date(), timeZone);
    const [measuredOn, setMeasuredOn] = useState(today);
    const [weight, setWeight] = useState('');
    const [length, setLength] = useState('');
    const [head, setHead] = useState('');
    const [notes, setNotes] = useState('');
    const [formError, setFormError] = useState<string | undefined>();
    const [indicator, setIndicator] = useState<GrowthIndicator>('weight');
    const birth = child.adjustedDateOfBirth ?? child.dateOfBirth;
    const rows = (growth.data ?? [])
        .slice()
        .sort((left, right) => left.measuredOn.localeCompare(right.measuredOn));

    function onSubmit(event: SubmitEvent<HTMLFormElement>): void {
        event.preventDefault();
        const weightG = weight.trim().length === 0 ? null : massToGrams(Number(weight), massUnit);
        const lengthMm =
            length.trim().length === 0 ? null : lengthToMillimetres(Number(length), lengthUnit);
        const headMm =
            head.trim().length === 0 ? null : lengthToMillimetres(Number(head), lengthUnit);
        if (
            (weightG !== null && !Number.isFinite(weightG)) ||
            (lengthMm !== null && !Number.isFinite(lengthMm)) ||
            (headMm !== null && !Number.isFinite(headMm))
        ) {
            setFormError('Enter numbers for the measurements you have.');
            return;
        }
        if (weightG === null && lengthMm === null && headMm === null) {
            setFormError('Add a weight, length, or head measurement.');
            return;
        }
        setFormError(undefined);
        create.mutate(
            {
                childId: child.id,
                measuredOn,
                weightG,
                lengthMm,
                headMm,
                notes,
            },
            {
                onSuccess: () => {
                    setWeight('');
                    setLength('');
                    setHead('');
                    setNotes('');
                },
            },
        );
    }

    return (
        <Card title="Growth" headingLevel={2}>
            {growth.error ? <FormError message={errorText(growth.error)} /> : null}
            <SegmentedControl
                label="Measurement"
                value={indicator}
                options={indicators}
                onChange={setIndicator}
            />
            <Suspense fallback={<p className={styles.meta}>Loading growth curves</p>}>
                <GrowthCurves
                    rows={rows}
                    birth={birth}
                    sex={child.sex}
                    indicator={indicator}
                    massUnit={massUnit}
                    lengthUnit={lengthUnit}
                    canLog={canLog}
                    onDelete={(id) => {
                        remove.mutate(id);
                    }}
                />
            </Suspense>
            {canLog ? (
                <form className={fieldStyles.stack} onSubmit={onSubmit}>
                    <TextField
                        label="Date"
                        type="date"
                        value={measuredOn}
                        onChange={(event) => {
                            setMeasuredOn(event.target.value);
                        }}
                        required
                    />
                    <TextField
                        label={`Weight (${massUnit})`}
                        inputMode="decimal"
                        value={weight}
                        onChange={(event) => {
                            setWeight(event.target.value);
                        }}
                    />
                    <TextField
                        label={`Length (${lengthUnit})`}
                        inputMode="decimal"
                        value={length}
                        onChange={(event) => {
                            setLength(event.target.value);
                        }}
                    />
                    <TextField
                        label={`Head (${lengthUnit})`}
                        inputMode="decimal"
                        value={head}
                        onChange={(event) => {
                            setHead(event.target.value);
                        }}
                    />
                    <TextField
                        label="Notes"
                        value={notes}
                        onChange={(event) => {
                            setNotes(event.target.value);
                        }}
                    />
                    {formError !== undefined ? <FormError message={formError} /> : null}
                    {create.error ? <FormError message={errorText(create.error)} /> : null}
                    <Button type="submit" loading={create.isPending}>
                        Save measurement
                    </Button>
                </form>
            ) : null}
        </Card>
    );
}
